// src/components/ClientImportModal.tsx
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Box,
} from "@mui/material";
import React, { Suspense } from "react";

import { ImportProvider, useImportContext } from "0.Mine/2.Context/importContext";
import { CustomField } from "0.Mine/3.Interfaces/Client";

// Lazy-load each step by mapping named exports to default
const FileUploadStep = React.lazy(() => import("./StepFileUpload"));
const MappingStep = React.lazy(() => import("./StepMapping"));
const ValidationStep = React.lazy(() => import("./StepValidation"));
const ImportStep = React.lazy(() => import("./StepImport"));

interface ClientImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (count: number) => void;
  customFields: any[];
  addCustomFields: (fields: CustomField) => void;
}

const ClientImportModalContent: React.FC<Omit<ClientImportModalProps, "open">> = ({
  onClose,
  onSuccess,
  customFields,
  addCustomFields,
}) => {
  const { activeStep, setActiveStep } = useImportContext();
  const steps = ["Upload", "Map", "Validate", "Import"];

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent dividers>
        <Suspense
          fallback={
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          }
        >
          {activeStep === 0 && <FileUploadStep />}
          {activeStep === 1 && <MappingStep addCustomField={addCustomFields} />}
          {activeStep === 2 && <ValidationStep />}
          {activeStep === 3 && <ImportStep onSuccess={onSuccess} />}
        </Suspense>
      </DialogContent>

      <DialogActions>
        {activeStep > 0 && (
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>
        )}
        <Box sx={{ flex: "1 1 auto" }} />
        {activeStep < steps.length - 1 && (
          <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={handleNext}>
            Next
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export const ClientImportModal: React.FC<ClientImportModalProps> = ({
  open,
  onClose,
  onSuccess,
  customFields,
  addCustomFields,
}) => (
  <ImportProvider customFields={customFields}>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <ClientImportModalContent
        onClose={onClose}
        onSuccess={onSuccess}
        customFields={customFields}
        addCustomFields={addCustomFields}
      />
    </Dialog>
  </ImportProvider>
);

export default ClientImportModal;
